import { Button, Dropdown, Tooltip } from 'antd'
import type { MenuProps } from 'antd'
import {
  ArrowLeft,
  Bot,
  Download,
  Link2,
  MoreHorizontal,
  Pencil,
  Sparkles,
  Trash2,
  Unlock,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { BackButton, HeaderInfo, PageTitle, PageSubtitle } from '../../../styles/pageHeaderStyles'
import {
  InsightsPageHeader,
  InsightsTitleRow,
  InsightsActionsRow,
} from './SurveyInsightsPageStyles'

export interface SurveyInsightsHeaderProps {
  parentLabel: string
  questionCount: number
  isClosed: boolean
  hasResponses: boolean
  isCompleting: boolean
  isReopening: boolean
  simulating: boolean
  mindmapId: string
  nodeId: string
  shareMenuItems: MenuProps['items']
  onComplete: () => void
  onReopen: () => void
  onSimulate: () => void
  onDelete: () => void
}

/**
 * Header chrome for the survey insights page: back button, title, and the
 * status-aware action cluster (Edit / Complete or Reopen / Share / Simulate /
 * ⋯ overflow menu).
 */
export function SurveyInsightsHeader({
  parentLabel,
  questionCount,
  isClosed,
  hasResponses,
  isCompleting,
  isReopening,
  simulating,
  mindmapId,
  nodeId,
  shareMenuItems,
  onComplete,
  onReopen,
  onSimulate,
  onDelete,
}: SurveyInsightsHeaderProps) {
  const navigate = useNavigate()

  return (
    <InsightsPageHeader>
      <InsightsTitleRow>
        <BackButton
          onClick={() => navigate(`/mindmap/${mindmapId}/research`, { replace: true })}
          title="Back"
        >
          <ArrowLeft size={18} />
        </BackButton>
        <HeaderInfo>
          <PageTitle>{parentLabel || 'Untitled survey'}</PageTitle>
          <PageSubtitle>
            {questionCount} question{questionCount !== 1 ? 's' : ''}
          </PageSubtitle>
        </HeaderInfo>
      </InsightsTitleRow>
      <InsightsActionsRow>
        {!isClosed && (
          <Button
            icon={<Pencil size={15} />}
            onClick={() => void navigate(`/mindmap/${mindmapId}/survey/${nodeId}/builder`)}
          >
            Edit
          </Button>
        )}
        {!isClosed ? (
          <Tooltip title={hasResponses ? '' : 'Collect at least one response before analyzing.'}>
            <Button
              type="primary"
              icon={<Sparkles size={15} />}
              loading={isCompleting}
              disabled={isCompleting || !hasResponses}
              onClick={onComplete}
            >
              Complete & analyze
            </Button>
          </Tooltip>
        ) : (
          <Button
            icon={<Unlock size={15} />}
            loading={isReopening}
            disabled={isReopening}
            onClick={onReopen}
          >
            Reopen
          </Button>
        )}
        <Dropdown menu={{ items: shareMenuItems }} trigger={['click']} placement="bottomRight">
          <Button icon={<Link2 size={15} />} disabled={isClosed}>
            Share
          </Button>
        </Dropdown>
        {!isClosed && (
          <Button
            icon={<Bot size={16} />}
            loading={simulating}
            disabled={simulating}
            onClick={onSimulate}
          >
            Simulate
          </Button>
        )}
        <Dropdown
          trigger={['click']}
          placement="bottomRight"
          menu={{
            items: [
              {
                key: 'export',
                icon: <Download size={13} />,
                label: 'Export PDF',
                onClick: () => window.print(),
              },
              { type: 'divider' },
              {
                key: 'delete',
                icon: <Trash2 size={13} />,
                label: 'Delete',
                danger: true,
                onClick: onDelete,
              },
            ],
          }}
        >
          <Button icon={<MoreHorizontal size={16} />} />
        </Dropdown>
      </InsightsActionsRow>
    </InsightsPageHeader>
  )
}
